import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Gift, Infinity as InfinityIcon, X } from "lucide-react";
import { Reveal, Overline } from "../Reveal";
import { PLANS, COMPARISON_SECTIONS, planPath } from "../../data/content";
import { useCurrency } from "../../contexts/CurrencyContext";
import { convertFromINR } from "../../data/currencies";

const VIEW_SUFFIX = { monthly: "/mo", yearly: "/yr", onetime: "lifetime" };

// Price shown under each plan name, for the billing `view` the page is on.
// Falls back to the plan's one-time or yearly tier when it has no such view.
function PlanPrice({ plan, view, convert, className = "" }) {
  const { currency } = useCurrency();
  const key = plan.pricing[view] ? view : plan.pricing.onetime ? "onetime" : "yearly";
  const tier = plan.pricing[key];
  if (!tier) return null;
  const money = (inr) => `${currency.symbol}${Math.round(convert(inr, currency)).toLocaleString("en-US")}`;
  return (
    <div data-testid="comparison-plan-price" className={className}>
      <span className="text-base font-extrabold text-[#FF6B00]">{money(tier.amount)}</span>{" "}
      <span className="text-xs font-semibold text-[#94A3B8]">{VIEW_SUFFIX[key]}</span>
      {tier.setup > 0 && (
        <div className="text-[11px] font-medium text-[#94A3B8]">+ {money(tier.setup)} setup</div>
      )}
    </div>
  );
}

// Tag on a row that carries the festival offer (`offer: true` in content.js).
function OfferBadge() {
  return (
    <span
      data-testid="comparison-offer-badge"
      className="ml-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#FF6B00] to-[#FF8533] px-2 py-0.5 align-middle text-[10px] font-extrabold uppercase tracking-wide text-white shadow-[0_4px_10px_rgba(255,107,0,0.35)]"
    >
      <Gift className="h-3 w-3" />
      Offer
    </span>
  );
}

// Row label - an external link when the row has an `href` (content.js).
function RowLabel({ row }) {
  if (!row.href) return row.label;
  return (
    <a
      href={row.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="comparison-row-link"
      className="underline decoration-[#FF6B00]/50 underline-offset-4 hover:text-[#FF6B00]"
    >
      {row.label}
    </a>
  );
}

// `unlimited` sections or rows (see content.js) show an infinity icon for an
// included row instead of a tick.
function Cell({ value, muted, unlimited, offer }) {
  if (value === true) {
    const Icon = unlimited ? InfinityIcon : Check;
    return (
      <Icon
        aria-label={unlimited ? "Unlimited" : "Included"}
        className={`mx-auto ${unlimited ? "w-5 h-5" : "w-4 h-4"} ${muted ? "text-[#CBD2E0]" : "text-[#FF6B00]"}`}
        strokeWidth={3}
      />
    );
  }
  if (value === false) return <X className="mx-auto w-4 h-4 text-[#CBD2E0]" strokeWidth={3} />;
  if (offer) return <span className="text-sm font-extrabold text-[#FF6B00]">{value}</span>;
  return <span className={`text-sm ${muted ? "text-[#94A3B8]" : "font-semibold text-[#0B1533]"}`}>{value}</span>;
}

// Same value, left-aligned with its own icon - used in the mobile per-plan
// list where there's no column to center within.
function MobileValue({ value, muted, unlimited, offer }) {
  if (value === true) {
    const Icon = unlimited ? InfinityIcon : Check;
    return (
      <Icon
        aria-label={unlimited ? "Unlimited" : "Included"}
        className={`${unlimited ? "w-5 h-5" : "w-4 h-4"} shrink-0 ${muted ? "text-[#CBD2E0]" : "text-[#FF6B00]"}`}
        strokeWidth={3}
      />
    );
  }
  if (value === false) return <X className="w-4 h-4 shrink-0 text-[#CBD2E0]" strokeWidth={3} />;
  if (offer) return <span className="text-sm font-extrabold text-[#FF6B00]">{value}</span>;
  return <span className={`text-sm ${muted ? "text-[#94A3B8]" : "font-bold text-[#0B1533]"}`}>{value}</span>;
}

function MobilePlanComparison({ plans, sections, view, convert }) {
  const [activeKey, setActiveKey] = useState(plans.find((p) => p.badge)?.key ?? plans[0].key);
  const active = plans.find((p) => p.key === activeKey) ?? plans[0];

  return (
    <div className="md:hidden">
      <div role="tablist" aria-label="Select a plan" className="flex gap-2 overflow-x-auto pb-1">
        {plans.map((p) => (
          <button
            key={p.key}
            role="tab"
            type="button"
            aria-selected={activeKey === p.key}
            onClick={() => setActiveKey(p.key)}
            className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold whitespace-nowrap transition-all ${
              activeKey === p.key
                ? "bg-[#FF6B00] text-white shadow-[0_6px_16px_rgba(255,107,0,0.3)]"
                : "border border-[#E6E9F1] bg-white text-[#475569]"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-[#E6E9F1] bg-white mb-card-shadow">
        <div className="flex items-center justify-between gap-3 border-b border-[#E6E9F1] px-5 py-4">
          <Link to={planPath(active)} className="text-sm font-bold text-[#0B1533] underline decoration-[#CBD2E0] underline-offset-4 hover:text-[#FF6B00]">
            {active.name}
          </Link>
          <PlanPrice plan={active} view={view} convert={convert} className="text-right" />
        </div>
        {sections.map((section) => (
          <div key={section.title} className={section.muted ? "bg-[#F9FAFC]" : ""}>
            <div
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wide ${
                section.muted ? "text-[#B8C0CE]" : "text-[#94A3B8] bg-[#F4F6FB]"
              }`}
            >
              {section.title}
            </div>
            <ul>
              {section.rows.map((row) => (
                <li
                  key={row.label}
                  className={`flex items-center justify-between gap-3 border-b px-5 py-3 last:border-0 ${
                    row.offer && row.values[active.key] !== false
                      ? "border-[#FF6B00]/30 bg-gradient-to-r from-[#FFF3E9] to-[#FFFBF6]"
                      : "border-[#E6E9F1]"
                  }`}
                >
                  <span className={`text-sm ${section.muted ? "text-[#B8C0CE]" : "text-[#475569]"}`}>
                    <RowLabel row={row} />
                    {row.offer && <OfferBadge />}
                  </span>
                  <MobileValue
                    value={row.values[active.key]}
                    muted={section.muted}
                    unlimited={section.unlimited || row.unlimited}
                    offer={row.offer}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function DesktopComparisonTable({ plans, sections, view, convert }) {
  return (
    <div className="hidden md:block overflow-x-auto rounded-xl border border-[#E6E9F1] bg-white mb-card-shadow">
      <table data-testid="pricing-comparison-table" className="w-full min-w-[640px] text-sm border-collapse">
        <thead>
          <tr className="border-b border-[#E6E9F1] bg-[#F9FAFC]">
            <th className="text-left px-6 py-5 font-bold text-[#0B1533]">Feature</th>
            {plans.map((p) => (
              <th key={p.key} className="px-6 py-5 font-bold text-[#0B1533] text-center align-top whitespace-nowrap">
                <Link to={planPath(p)} data-testid="comparison-plan-link" className="hover:text-[#FF6B00] hover:underline">
                  {p.name}
                </Link>
                <PlanPrice plan={p} view={view} convert={convert} className="mt-1 whitespace-nowrap font-normal" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => (
            <Fragment key={section.title}>
              <tr className={section.muted ? "bg-[#F9FAFC]" : "bg-[#F4F6FB]"}>
                <td
                  colSpan={plans.length + 1}
                  className={`px-6 py-2.5 text-xs font-bold uppercase tracking-wide ${
                    section.muted ? "text-[#B8C0CE]" : "text-[#94A3B8]"
                  }`}
                >
                  {section.title}
                </td>
              </tr>
              {section.rows.map((row) => (
                <tr
                  key={row.label}
                  className={`border-b last:border-0 transition-colors ${
                    row.offer
                      ? "border-[#FF6B00]/30 bg-gradient-to-r from-[#FFF3E9] to-[#FFFBF6]"
                      : `border-[#E6E9F1] ${section.muted ? "" : "hover:bg-[#F9FAFC]"}`
                  }`}
                >
                  <td className={`px-6 py-4 ${row.offer ? "font-bold text-[#0B1533]" : section.muted ? "text-[#B8C0CE]" : "text-[#475569]"}`}>
                    <RowLabel row={row} />
                    {row.offer && <OfferBadge />}
                  </td>
                  {plans.map((p) => (
                    <td key={p.key} className="px-6 py-4 text-center">
                      <Cell
                        value={row.values[p.key]}
                        muted={section.muted}
                        unlimited={section.unlimited || row.unlimited}
                        offer={row.offer}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Key under the table - only the symbols and terms the current table uses.
function Legend({ sections }) {
  const cells = sections.flatMap((sec) =>
    sec.rows.flatMap((row) =>
      Object.values(row.values).map((v) => (v === true && (sec.unlimited || row.unlimited) ? "unlimited" : v))
    )
  );
  const has = (v) => cells.includes(v);
  const items = [
    has(true) && { key: "yes", icon: <Check className="h-4 w-4 text-[#FF6B00]" strokeWidth={3} />, text: "Included in the plan" },
    has(false) && { key: "no", icon: <X className="h-4 w-4 text-[#CBD2E0]" strokeWidth={3} />, text: "Not included in the plan" },
    has("unlimited") && {
      key: "unlimited",
      icon: <InfinityIcon className="h-5 w-5 text-[#FF6B00]" strokeWidth={3} />,
      text: "Unlimited - use it as much as you like, it never counts towards your channel limit",
    },
    has("Within Limit") && {
      key: "within",
      icon: <span className="text-xs font-bold text-[#0B1533] whitespace-nowrap">Within Limit</span>,
      text: "Available, and each one you connect counts towards the plan's Total Channels",
    },
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <ul
      data-testid="pricing-comparison-legend"
      className="mt-5 grid gap-x-8 gap-y-3 rounded-xl border border-[#E6E9F1] bg-white px-5 py-4 text-sm text-[#475569] sm:grid-cols-2"
    >
      {items.map((item) => (
        <li key={item.key} className="flex items-start gap-3">
          <span className="mt-0.5 flex min-w-[20px] shrink-0 items-center justify-center">{item.icon}</span>
          <span className="leading-snug">{item.text}</span>
        </li>
      ))}
    </ul>
  );
}

// `view` is the billing view the page is showing (monthly | yearly | onetime)
// and `convert` its INR -> local-currency rule, same as PricingPlanCard.
export default function PricingComparisonTable({
  plans = PLANS,
  sections = COMPARISON_SECTIONS,
  view = "onetime",
  convert = convertFromINR,
}) {
  return (
    <section
      id="pricing-comparison"
      data-testid="pricing-comparison-section"
      className="relative pt-10 pb-20 md:pt-12 md:pb-24 border-t border-[#E6E9F1] bg-[#F4F6FB] scroll-mt-24"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <Overline>Features with Plans</Overline>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
            See Exactly What You're Getting
          </h2>
          <p className="mt-4 hidden md:block text-base md:text-lg text-[#475569] leading-relaxed">
            Every feature, every plan, no fine print - compare them all before you commit.
          </p>
          <p className="mt-3 text-sm text-[#94A3B8] md:hidden">Tap a plan below to see its full feature list.</p>
        </div>

        <Reveal delay={0.1} className="mt-6 md:mt-12">
          {/* key forces the mobile plan-tab state to reset when the level toggles */}
          <MobilePlanComparison key={plans[0]?.key} plans={plans} sections={sections} view={view} convert={convert} />
          <DesktopComparisonTable plans={plans} sections={sections} view={view} convert={convert} />
          <Legend sections={sections} />
        </Reveal>
      </div>
    </section>
  );
}
